/**
 * Hotjar - Analytics tracking component
 *
 * Loads Hotjar tracking script for authenticated users in production.
 * This component renders nothing visually - it only loads the tracking script.
 *
 * Usage:
 * Place this component inside AuthenticatedLayout so it only loads
 * after the user has logged in.
 */

import { useEffect } from 'react'
import { config } from '@/config'

// Hotjar site ID for Orchard Offers
const HOTJAR_ID = 6621893
const HOTJAR_VERSION = 6

// Extend window type for Hotjar
interface HotjarFunction {
  (...args: unknown[]): void
  q?: unknown[][]
}

declare global {
  interface Window {
    hj?: HotjarFunction
    _hjSettings?: { hjid: number; hjsv: number }
  }
}

export function Hotjar() {
  useEffect(() => {
    // Only load Hotjar in production
    if (config.sentryEnvironment !== 'prod') {
      return
    }

    // Don't load if already initialized
    if (window.hj) {
      return
    }

    // Initialize Hotjar queue function
    const hjFunc: HotjarFunction = function (...args: unknown[]) {
      const queue = hjFunc.q || []
      queue.push(args)
      hjFunc.q = queue
    }
    hjFunc.q = hjFunc.q || []
    window.hj = hjFunc
    window._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_VERSION }

    // Load the Hotjar script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_VERSION}`
    document.head.appendChild(script)

    // Cleanup on unmount (though this rarely happens in practice)
    return () => {
      // Note: We don't remove the script as Hotjar doesn't support clean unloading
    }
  }, [])

  // This component renders nothing
  return null
}
